import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import { Box, Stack, Typography } from "@mui/material";
import { ReactNode, useRef, useState } from "react";
import { useOutsideClick } from "../../../hooks/useOutSideClick";
import { TriangleIcon } from "../../images";

type Item = {
  value: string;
  icon?: ReactNode;
};

type Props = {
  items: Item[];
  label: string;
  placeholder: string;
  selectedItem: Item;
  onChange: (item: Item) => void;
  maxHeight?: string;
  maxItemsWidth?: string;
};

export const Dropdown = ({
  items,
  label,
  placeholder,
  selectedItem,
  onChange,
  maxHeight = "124px",
  maxItemsWidth = "100%",
}: Props) => {
  const { value, icon } = selectedItem;
  const dropdownWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const closeHandler = () => {
    setIsOpen(false);
  };

  const handleChange = (item: Item) => {
    onChange(item);
    closeHandler();
  };

  useOutsideClick({
    elementRef: dropdownRef,
    onOutsideClick: closeHandler,
    isOpened: isOpen,
    wrapperRef: dropdownWrapperRef,
  });

  return (
    <Stack gap="4px" flexGrow={1}>
      {label && (
        <Typography
          sx={{
            fontSize: "12px",
            color: "#1F293299",
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Typography>
      )}

      <div
        ref={dropdownWrapperRef}
        style={{
          position: "relative",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 40px 8px 16px",
            cursor: "pointer",
            border: "1px solid #CFCFCF",
            borderRadius: "8px",
            height: "40px",
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          {placeholder && !value && (
            <Typography
              component="span"
              variant="button"
              color="custom.darkGrey"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {placeholder}
            </Typography>
          )}

          {icon && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "absolute",
                left: "8px",
                top: "8px",
              }}
            >
              {icon}
            </Box>
          )}

          {value && (
            <Typography
              component="span"
              variant="button"
              color="custom.textDarkBlue"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingLeft: icon ? "20px" : "0",
              }}
            >
              {value}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "absolute",
              right: "16px",
              top: "18px",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
            }}
          >
            <TriangleIcon />
          </Box>
        </Box>

        {isOpen && (
          <Box
            ref={dropdownRef}
            sx={{
              backgroundColor: "white",
              position: "absolute",
              zIndex: 9999,
              left: 0,
              top: 50,
              boxShadow: "0px 2px 8px 0px #00000040",
              borderRadius: "8px",
              paddingY: "8px",
              width: maxItemsWidth,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                maxHeight,
                overflow: "auto",
              }}
            >
              {items.map((el) => (
                <MenuItem
                  sx={{
                    padding: "4px",
                  }}
                  key={el.value}
                  value={el.value}
                  onClick={() => handleChange(el)}
                  selected={el.value === value}
                >
                  {el.icon}
                  <ListItemText
                    sx={{
                      padding: "8px",
                    }}
                    primaryTypographyProps={{
                      sx: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                    primary={el.value}
                  />
                </MenuItem>
              ))}
            </Box>
          </Box>
        )}
      </div>
    </Stack>
  );
};
